import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDashboards } from '../../hooks/useDashboards';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Loader2, Eye, Plus, LayoutDashboard } from 'lucide-react';
import type { Dashboard } from '../../services/dashboard.service';
import { dashboardService } from '../../services/dashboard.service';

function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return 'Invalid date';
  }
}

export default function Dashboards() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  const { data, isLoading, error } = useDashboards({
    page,
    pageSize,
  });

  const dashboards = data?.records || [];
  const totalPages = data?.total_pages || 1;

  const handleViewDashboard = (dashboardId: string) => {
    navigate(`/insights/dashboards/${dashboardId}`);
  };

  const handleCreateDashboard = async () => {
    if (!dashboardName.trim()) {
      return;
    }

    setIsCreating(true);
    try {
      // Create a default dashboard with empty components
      const newDashboard: Omit<Dashboard, 'id' | 'last_updated'> = {
        name: dashboardName.trim(),
        description: dashboardDescription.trim() || '',
        components: [],
        version_idx: 0,
      };

      const createdDashboard = await dashboardService.create(newDashboard);
      
      // Close dialog and reset form
      setIsCreateDialogOpen(false);
      setDashboardName('');
      setDashboardDescription('');
      
      // Navigate to the new dashboard
      navigate(`/insights/dashboards/${createdDashboard.id}`);
    } catch (error) {
      console.error('Failed to create dashboard:', error);
      // TODO: Show error toast
      alert('Failed to create dashboard. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {/* Breadcrumbs */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/insights">Insights</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Dashboards</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {/* Page Title */}
          <h1 className="text-xl font-semibold text-foreground">Dashboards</h1>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Dashboard
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading dashboards...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load dashboards. Please try again.
            </p>
          </div>
        ) : dashboards.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No dashboards found. Create your first dashboard to get started.</p>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Components</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboards.map((dashboard: Dashboard) => (
                    <TableRow key={dashboard.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                          {dashboard.name || 'Untitled Dashboard'}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {dashboard.description || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {dashboard.components?.length || 0} component{(dashboard.components?.length || 0) !== 1 ? 's' : ''}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(dashboard.last_updated)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDashboard(dashboard.id)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) setPage(page - 1);
                        }}
                        style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(pageNum);
                              }}
                              isActive={pageNum === page}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={`ellipsis-${pageNum}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}
                    
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) setPage(page + 1);
                        }}
                        style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Dashboard Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Dashboard</DialogTitle>
            <DialogDescription>
              Enter a name and optional description for your new dashboard. You can configure it after creation.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Input
              placeholder="Dashboard name"
              value={dashboardName}
              onChange={(e) => setDashboardName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dashboardName.trim() && !isCreating) {
                  handleCreateDashboard();
                }
              }}
              autoFocus
            />
            <Input
              placeholder="Description (optional)"
              value={dashboardDescription}
              onChange={(e) => setDashboardDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dashboardName.trim() && !isCreating) {
                  handleCreateDashboard();
                }
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                setDashboardName('');
                setDashboardDescription('');
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDashboard}
              disabled={!dashboardName.trim() || isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


